import { Color, Layer } from '@deck.gl/core';
import { smooth } from '@fe/core';
import { IFlightItem, LngLatArray } from '../flights/types';
import { DeckLayerCreator } from './deck-layer-creator';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import { AnimatedLineLayer } from './layers/line/animated-line-layer';
import { HidablePathLayer } from './layers/path/hidable-path-layer';

export class DeckFlightHistory extends DeckLayerCreator {
    constructor(plugin: DeckOverlayPlugin) {
        super(plugin);
    }

    createLayers(data: IFlightItem[]): Layer[] {
        if (!this.props?.flights.active.visible) {
            return [];
        }

        const { historySmoothing, historyColor, historyModern, historyUseAircraftColor, defaultFlightColor } = this.props.flights.active;
        const getTrailColor = (d: IFlightItem): Color =>
            historyUseAircraftColor ? ((d.alertState?.color ?? d.filterColor ?? defaultFlightColor) as Color) : (historyColor as Color);

        const modern = historyModern;
        const cache = new WeakMap<IFlightItem, TrailGeometry>();
        const getGeometry = (d: IFlightItem): TrailGeometry => {
            let geometry = cache.get(d);
            if (!geometry) {
                const path = historySmoothing ? smooth(d.history, this.props!.smoothingParams) : d.history;
                geometry = modern ? resampleTrail(path) : { path, progress: [] };
                cache.set(d, geometry);
            }
            return geometry;
        };
        const getPath = (d: IFlightItem) => getGeometry(d).path;
        const trailColors = (d: IFlightItem, alpha: number, exponent: number, whiten: number, minWhite = 0) =>
            this.getCometColors(getGeometry(d).progress, getTrailColor(d), alpha, exponent, whiten, minWhite);
        const trailTriggers = (widthKey: string) => ({
            all: [this.flightsVersion],
            getPath: [historySmoothing, modern],
            getColor: [this.propsVersion, modern, historyUseAircraftColor],
            getWidth: [widthKey],
            getIsHidden: this.getIsHiddenUpdateTrigger
        });

        const layers: Layer[] = [];

        if (modern) {
            // Layered glow (wide/faint -> narrow/strong), then a saturated core and a hot white filament
            const glow = (id: string, width: number, alpha: number) =>
                new HidablePathLayer<IFlightItem>({
                    id,
                    data,
                    pickable: false,
                    widthUnits: 'pixels',
                    widthMinPixels: width,
                    widthMaxPixels: width,
                    wrapLongitude: true,
                    capRounded: true,
                    jointRounded: true,
                    getWidth: width,
                    getPath,
                    getColor: (d) => trailColors(d, alpha, 1.5, 0),
                    getIsHidden: (d) => this.getIsHidden(d),
                    updateTriggers: trailTriggers(id),
                    parameters: this.plugin.getRenderingParams()
                });

            layers.push(glow('history-glow-outer', 6, 0.14), glow('history-glow-mid', 4, 0.3), glow('history-glow-inner', 3, 0.55));

            layers.push(
                new HidablePathLayer<IFlightItem>({
                    id: 'history',
                    data,
                    widthMinPixels: 1.6,
                    widthMaxPixels: 1.6,
                    wrapLongitude: true,
                    capRounded: true,
                    jointRounded: true,
                    getColor: (d) => trailColors(d, 1, 1.2, 0.25),
                    getWidth: 1.6,
                    getPath,
                    getIsHidden: (d) => this.getIsHidden(d),
                    updateTriggers: trailTriggers('history'),
                    parameters: this.plugin.getRenderingParams()
                }),
                new HidablePathLayer<IFlightItem>({
                    id: 'history-filament',
                    data,
                    pickable: false,
                    widthMinPixels: 0.8,
                    widthMaxPixels: 0.8,
                    wrapLongitude: true,
                    capRounded: true,
                    jointRounded: true,
                    getColor: (d) => trailColors(d, 0.9, 3, 1, 0.35),
                    getWidth: 0.8,
                    getPath,
                    getIsHidden: (d) => this.getIsHidden(d),
                    updateTriggers: trailTriggers('history-filament'),
                    parameters: this.plugin.getRenderingParams()
                })
            );
        } else {
            layers.push(
                new HidablePathLayer<IFlightItem>({
                    id: 'history',
                    data,
                    widthMinPixels: 2,
                    widthMaxPixels: 2,
                    wrapLongitude: true,
                    getColor: historyUseAircraftColor ? getTrailColor : historyColor,
                    getWidth: 2,
                    getPath,
                    getIsHidden: (d) => this.getIsHidden(d),
                    updateTriggers: trailTriggers('history'),
                    parameters: this.plugin.getRenderingParams()
                })
            );
        }
        if (this.isCoastMode) {
            const historyLastSegmentLayer = new AnimatedLineLayer({
                id: 'history-last-segment',
                data,
                pickable: false,
                getWidth: 2,
                wrapLongitude: true,
                positionTexture: this.plugin.positionTexture,
                getTargetPosition: (d) => this.getFlightPosition(d),
                getColor: (d) => getTrailColor(d) as Color,
                getIsHidden: (d) => this.getIsHidden(d),
                getId: (d) => d.id,
                updateTriggers: {
                    all: [this.flightsVersion],
                    getColor: [this.propsVersion, historyUseAircraftColor],
                    getSourcePosition: [this.propsVersion],
                    getIsHidden: this.getIsHiddenUpdateTrigger
                },
                parameters: this.plugin.getRenderingParams()
            });

            layers.push(historyLastSegmentLayer);
        }

        return layers;
    }

    private getCometColors(progress: number[], color: Color, alphaScale: number, exponent: number, whiten: number, minWhite = 0): Color[] {
        const [r, g, b] = color;
        const baseAlpha = (color[3] ?? 255) * alphaScale;
        return progress.map((t) => {
            const w = Math.max(minWhite, whiten * Math.pow(t, 4));
            return [r + (255 - r) * w, g + (255 - g) * w, b + (255 - b) * w, baseAlpha * Math.pow(t, exponent)] as Color;
        });
    }
}

interface TrailGeometry {
    path: LngLatArray[];
    progress: number[]; // 0 at tail, 1 at head, per vertex
}

const TRAIL_MAX_POINTS = 160;

function resampleTrail(path: LngLatArray[]): TrailGeometry {
    const n = path.length;
    if (n < 2) {
        return { path, progress: path.map(() => 1) };
    }

    const cumulative = [0];
    for (let i = 1; i < n; i++) {
        const [x0, y0] = path[i - 1];
        const [x1, y1] = path[i];
        const dx = shortestLngDelta(x0, x1) * Math.cos(((y0 + y1) / 2) * (Math.PI / 180));
        cumulative.push(cumulative[i - 1] + Math.hypot(dx, y1 - y0));
    }
    const total = cumulative[n - 1];
    if (total <= 0) {
        return { path, progress: path.map((_, i) => i / (n - 1)) };
    }

    const count = Math.min(TRAIL_MAX_POINTS, Math.max(n, 32));
    const out: LngLatArray[] = [];
    const progress: number[] = [];
    let seg = 1;
    for (let k = 0; k < count; k++) {
        const t = k / (count - 1);
        const dist = t * total;
        while (seg < n - 1 && cumulative[seg] < dist) {
            seg++;
        }
        const span = cumulative[seg] - cumulative[seg - 1];
        const f = span > 0 ? (dist - cumulative[seg - 1]) / span : 1;
        const [x0, y0] = path[seg - 1];
        const [x1, y1] = path[seg];
        out.push([normalizeLng(x0 + shortestLngDelta(x0, x1) * f), y0 + (y1 - y0) * f] as LngLatArray);
        progress.push(t);
    }
    return { path: out, progress };
}

function shortestLngDelta(a: number, b: number): number {
    let d = b - a;
    if (d > 180) d -= 360;
    else if (d < -180) d += 360;
    return d;
}

function normalizeLng(lng: number): number {
    return lng > 180 ? lng - 360 : lng < -180 ? lng + 360 : lng;
}
